import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Gavel,
  Sparkles,
  Crown,
  ArrowRight,
  ArrowLeft,
  Send,
  Loader2,
  ShieldCheck,
  BookOpen,
  ScrollText,
  Award,
  Mic,
  FolderOpenDot,
  RotateCcw,
} from "lucide-react";

const PROFILES = [
  {
    id: "estricto",
    label: "Profesor Estricto",
    icon: ShieldCheck,
    tagline: "Rigor extremo. Cero tolerancia a ambigüedades.",
    color: "bg-forest",
  },
  {
    id: "metodologo",
    label: "Profesor Metodólogo",
    icon: BookOpen,
    tagline: "Diseño, validez, muestreo y análisis.",
    color: "bg-[#A67C52]",
  },
  {
    id: "linguista",
    label: "Profesor Lingüista",
    icon: ScrollText,
    tagline: "Coherencia textual, citas y argumentación.",
    color: "bg-[#7E9A7C]",
  },
  {
    id: "practico",
    label: "Profesor Práctico",
    icon: Mic,
    tagline: "“¿Y eso para qué sirve en la práctica?”",
    color: "bg-[#4A4A55]",
  },
];

export default function JuryView() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPremium = user?.plan === "premium";
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [profile, setProfile] = useState("estricto");
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data.projects || []);
      } catch {
        toast.error("No se pudieron cargar tus proyectos");
      } finally {
        setLoadingProjects(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [session?.turn_index, submitting]);

  const start = async () => {
    if (!isPremium) {
      toast.error("Simulador disponible solo en Premium.");
      return;
    }
    if (!selectedProject) return;
    setStarting(true);
    try {
      const { data } = await api.post(
        `/projects/${selectedProject.project_id}/jury/start`,
        { profile }
      );
      setSession(data);
      toast.success("¡Tu jurado está listo! Responde con calma.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el simulador");
    } finally {
      setStarting(false);
    }
  };

  const submit = async () => {
    if (!answer.trim() || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/projects/${selectedProject.project_id}/jury/${session.session_id}/answer`,
        { text: answer.trim() }
      );
      setSession(data);
      setAnswer("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar la respuesta");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSession(null);
    setAnswer("");
  };

  /* ----- Locked free-user view ----- */
  if (!isPremium) {
    return (
      <div className="px-6 lg:px-12 py-8 lg:py-10 max-w-5xl mx-auto" data-testid="jury-view">
        <Intro />
        <LockedBanner />
        <ProfilesPreview profile={profile} setProfile={setProfile} disabled />
      </div>
    );
  }

  /* ----- Project picker (no session active) ----- */
  if (!session) {
    return (
      <div className="px-6 lg:px-12 py-8 lg:py-10 max-w-5xl mx-auto" data-testid="jury-view">
        <Intro />

        {/* Project picker */}
        <section className="mt-8">
          <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-3">
            1. Elige el proyecto a defender
          </p>
          {loadingProjects ? (
            <p className="text-ink-soft text-[13px]">Cargando proyectos…</p>
          ) : projects.length === 0 ? (
            <div className="rounded-2xl border border-rule bg-white p-6 text-center">
              <p className="font-serif text-xl text-ink">Aún no tienes proyectos.</p>
              <button
                onClick={() => navigate("/dashboard")}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark"
              >
                Crear mi primer proyecto
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="jury-projects">
              {projects.map((p) => (
                <button
                  key={p.project_id}
                  data-testid={`jury-project-${p.project_id}`}
                  onClick={() => setSelectedProject(p)}
                  className={`text-left rounded-xl border p-4 transition-all ${
                    selectedProject?.project_id === p.project_id
                      ? "border-forest bg-white ring-2 ring-forest/15"
                      : "border-rule bg-white/60 hover:bg-white"
                  }`}
                >
                  <span className="grid place-items-center h-8 w-8 rounded-lg bg-forest/10 text-forest">
                    <FolderOpenDot size={14} />
                  </span>
                  <p className="mt-3 font-serif text-[15px] text-ink leading-tight line-clamp-2">{p.title}</p>
                  <p className="mt-1 text-[11.5px] text-ink-soft">{p.career}</p>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Profile selector */}
        <section className="mt-10">
          <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-3">
            2. Elige el perfil del jurado
          </p>
          <ProfilesPreview profile={profile} setProfile={setProfile} />
        </section>

        <section className="mt-8">
          <button
            data-testid="jury-start-btn"
            onClick={start}
            disabled={!selectedProject || starting}
            className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all disabled:opacity-60 shadow-[0_10px_30px_-12px_rgba(39,59,44,0.55)] hover:shadow-[0_18px_40px_-12px_rgba(39,59,44,0.65)] hover:-translate-y-[1px]"
          >
            {starting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Preparando jurado…
              </>
            ) : (
              <>
                <Gavel size={14} />
                Iniciar simulación
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>
          {!selectedProject && (
            <p className="mt-2 text-[12px] text-ink-soft">Selecciona un proyecto para continuar.</p>
          )}
        </section>
      </div>
    );
  }

  /* ----- Active / completed session ----- */
  const currentIndex = Math.min(session.turn_index, 2);
  const isCompleted = session.status === "completed";
  return (
    <div className="px-6 lg:px-12 py-6 lg:py-8 max-w-5xl mx-auto" data-testid="jury-view">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={reset}
          data-testid="jury-exit-btn"
          className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-soft hover:text-forest"
        >
          <ArrowLeft size={13} /> Salir del simulador
        </button>
        <span className="h-4 w-px bg-rule" />
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
          {PROFILES.find((p) => p.id === session.profile)?.label}
        </p>
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-paper-2 px-3 py-1 text-[11px] font-medium text-ink-soft">
          Pregunta {isCompleted ? "3" : currentIndex + 1}/3
        </span>
      </div>

      <h1
        className="mt-3 font-serif text-2xl sm:text-3xl text-ink leading-tight text-balance"
        data-testid="jury-project-title"
      >
        {selectedProject.title}
      </h1>

      {/* Progress dots */}
      <div className="mt-5 flex items-center gap-1.5">
        {[0, 1, 2].map((i) => {
          const done = i < session.turn_index;
          const active = i === session.turn_index && !isCompleted;
          return (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                done ? "bg-forest w-8" : active ? "bg-copper w-12" : "bg-rule w-8"
              }`}
            />
          );
        })}
      </div>

      {/* Scroller */}
      <div
        ref={scrollerRef}
        data-testid="jury-scroller"
        className="mt-6 flex flex-col gap-4 max-h-[55vh] overflow-y-auto pr-1"
      >
        {(session.qa || []).map((qa, i) => (
          <div key={i} className="rounded-2xl border border-rule bg-white p-5" data-testid={`jury-turn-${i}`}>
            <div className="flex items-center gap-2">
              <span className="grid place-items-center h-7 w-7 rounded-full bg-forest text-paper">
                <Gavel size={12} />
              </span>
              <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
                Pregunta {i + 1}
              </p>
            </div>
            <p className="mt-3 font-serif text-lg text-ink leading-snug">
              {qa.question}
            </p>

            {qa.answer && (
              <div className="mt-4 rounded-xl border border-rule bg-paper-2/40 p-3">
                <p className="text-[10px] tracking-[0.22em] uppercase text-ink-soft/80 font-medium mb-1">
                  Tu respuesta
                </p>
                <p className="text-[13px] text-ink whitespace-pre-wrap">{qa.answer}</p>
              </div>
            )}

            {qa.evaluation && (
              <div className="mt-3 rounded-xl border border-copper/30 bg-[#FBF6EC] p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium inline-flex items-center gap-1.5">
                    <ShieldCheck size={11} /> Veredicto
                  </p>
                  {typeof qa.score === "number" && (
                    <span
                      data-testid={`jury-score-${i}`}
                      className={`text-[11px] font-medium ${
                        qa.score >= 75 ? "text-forest" : qa.score >= 50 ? "text-copper" : "text-[#B6533F]"
                      }`}
                    >
                      {qa.score}/100
                    </span>
                  )}
                </div>
                <p className="text-[13px] text-ink leading-relaxed">{qa.evaluation}</p>
              </div>
            )}
          </div>
        ))}

        {/* Submitting indicator */}
        {submitting && (
          <div className="rounded-2xl border border-rule bg-white p-5 inline-flex items-center gap-2" data-testid="jury-evaluating">
            <Loader2 size={14} className="animate-spin text-forest" />
            <span className="text-[13px] text-ink-soft">El jurado está evaluando tu respuesta…</span>
          </div>
        )}
      </div>

      {/* Answer form OR final verdict */}
      {!isCompleted ? (
        <div className="mt-5 rounded-2xl border border-rule bg-white p-4" data-testid="jury-answer-form">
          <p className="text-[10px] tracking-[0.22em] uppercase text-ink-soft/80 font-medium mb-2">
            Tu respuesta
          </p>
          <textarea
            data-testid="jury-answer-input"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={4}
            disabled={submitting}
            placeholder="Responde como si estuvieras frente al jurado. Sé claro y argumenta."
            className="w-full px-3 py-2.5 rounded-lg border border-rule bg-paper-2/30 focus:bg-white text-[13.5px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 resize-none disabled:opacity-60"
          />
          <div className="mt-3 flex items-center justify-between">
            <p className="text-[11px] text-ink-soft">
              {answer.length}/4000 caracteres
            </p>
            <button
              data-testid="jury-submit-answer"
              onClick={submit}
              disabled={!answer.trim() || submitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark disabled:opacity-60"
            >
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {submitting ? "Enviando…" : "Enviar respuesta"}
            </button>
          </div>
        </div>
      ) : (
        <FinalVerdict session={session} onReset={reset} />
      )}
    </div>
  );
}

function Intro() {
  return (
    <>
      <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
        Simulador de jurado
      </p>
      <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink leading-tight text-balance">
        Entrena tu defensa antes del día del examen profesional.
      </h1>
      <p className="mt-3 text-[14px] text-ink-soft max-w-2xl">
        Elegimos un proyecto, eliges el perfil de sinodal y la IA te bombardea con 3 preguntas reales sobre tu tesis. Evalúa cada respuesta y te da un veredicto final.
      </p>
    </>
  );
}

function ProfilesPreview({ profile, setProfile, disabled = false }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="jury-profile-grid">
      {PROFILES.map((p) => {
        const active = profile === p.id;
        return (
          <button
            key={p.id}
            data-testid={`jury-profile-${p.id}`}
            onClick={() => !disabled && setProfile(p.id)}
            disabled={disabled}
            className={`text-left rounded-2xl border p-4 transition-all ${
              active
                ? "border-forest bg-white ring-2 ring-forest/15"
                : "border-rule bg-white/60 hover:bg-white"
            } ${disabled ? "opacity-90 cursor-not-allowed" : ""}`}
          >
            <span className={`grid place-items-center h-10 w-10 rounded-xl ${p.color} text-paper`}>
              <p.icon size={16} />
            </span>
            <p className="mt-3 font-serif text-lg text-ink leading-tight">{p.label}</p>
            <p className="mt-1 text-[12px] text-ink-soft leading-relaxed">{p.tagline}</p>
          </button>
        );
      })}
    </div>
  );
}

function LockedBanner() {
  return (
    <div className="mt-8 rounded-2xl border border-copper/30 bg-[#FBF6EC] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-testid="jury-locked">
      <div className="flex items-start gap-4">
        <span className="grid place-items-center h-10 w-10 rounded-full bg-forest text-paper">
          <Crown size={16} className="text-[#E0C58A]" />
        </span>
        <div>
          <p className="font-serif text-xl text-ink">El simulador completo es Premium</p>
          <p className="mt-1 text-[13px] text-ink-soft max-w-md">
            Te bombardeamos con preguntas reales, evaluamos cada respuesta y te damos un veredicto final con score.
          </p>
        </div>
      </div>
      <Link
        to="/pricing"
        data-testid="jury-upgrade-cta"
        className="group inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all self-start sm:self-auto whitespace-nowrap"
      >
        <Crown size={13} className="text-[#E0C58A]" />
        Activar Premium
        <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}

function FinalVerdict({ session, onReset }) {
  const score = session.overall_score || 0;
  const ring = score >= 75 ? "text-forest" : score >= 50 ? "text-copper" : "text-[#B6533F]";
  return (
    <div className="mt-6 rounded-2xl border border-forest/20 bg-white p-6" data-testid="jury-final-verdict">
      <div className="flex items-center gap-4">
        <div className="relative h-20 w-20 shrink-0">
          <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E8E6E1" strokeWidth="3" />
            <circle
              cx="18"
              cy="18"
              r="15.9"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeDasharray={`${score}, 100`}
              strokeLinecap="round"
              className={ring}
            />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <span data-testid="jury-overall-score" className="font-serif text-2xl text-ink leading-none">
              {score}
            </span>
          </div>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium inline-flex items-center gap-1.5">
            <Award size={11} /> Veredicto final
          </p>
          <p className="font-serif text-xl text-ink leading-snug mt-1" data-testid="jury-overall-feedback">
            {session.overall_feedback || "Defensa concluida."}
          </p>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          data-testid="jury-restart-btn"
          onClick={onReset}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] text-ink-soft hover:bg-paper-2"
        >
          <RotateCcw size={12} /> Nueva simulación
        </button>
      </div>
    </div>
  );
}
